import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ModelsEntitlementService } from '@ddt-web/common';
import { CommonDCR, ERROR_CODES, ErrorDCR } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ApiParamPosition, SharedApplicationConstants } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { AllAvailableFeatureFlags, InjectFeatureFlag } from '@ddt-web/feature-flag';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { Config, StoreService } from '@ddt-web/store-service';
import * as cloneDeep from 'lodash/cloneDeep';
import * as compact from 'lodash/compact';
import * as endsWith from 'lodash/endsWith';
import * as find from 'lodash/find';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as head from 'lodash/head';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as last from 'lodash/last';
import * as replace from 'lodash/replace';
import * as split from 'lodash/split';
import * as sumBy from 'lodash/sumBy';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

import { PortfolioData } from '../models';
import { AnalysisParams } from './analysis-params';
import { ApiService } from './api.service';
import { BenchmarkConfig } from './config/benchmark.config';
import { DataServiceApiConstants } from './constants/data-service-api.constants';
import { ApiSettings, ApiStoreConfiguration, PortfolioAnalysisOptions } from './models';
import { BenchmarkUtil } from './utils/benchmark.util';
import { TaxDataUtil } from './utils/tax-data.util';

@Injectable()
export class DataPortfolioAnalysisService {

	private static readonly WEIGHT_TOLERANCE = 0.01;

	@InjectFeatureFlag(AllAvailableFeatureFlags.FEATURE_FLAG_OPTIMISE_PERFORMANCE_API_CALLS) private readonly shouldOptimisePerformanceApis: boolean;
	@InjectConfig(SharedConfigKey.ENABLE_TPM_FOR_DOT_COM) private readonly isTpmEnabledForDotCom: boolean;
	@InjectConfig(SharedConfigKey.ENABLE_ADAM_MODEL_NAMES) private readonly enableAdamModelNames: boolean;
	@InjectConfig(SharedConfigKey.ENABLE_CMS_RENAMING) private readonly enableCmsRenaming: boolean;
	@InjectConfig(SharedConfigKey.SHOULD_MAKE_SIMPLE_PERFORMANCE_API_CALL) private readonly shouldMakeSimplePerformanceApiCall: boolean = false;
	@InjectConfig(SharedConfigKey.FLIP_MODELS_CONFIG_STORE_IDENTIFIER_TO_DAS_PORTFOLIO_ID) private readonly flipModelsConfigStoreIdentifierToDasPortfolioId: boolean = true;

	/**
	 * This service only deal with fetch and store data from /wds/services/portfolio-analysis
	 */
	constructor(
		private apiService: ApiService,
		protected storeService: StoreService,
		protected contentService: ContentService,
		private modelsEntitlementService: ModelsEntitlementService
	) { }

	public fetchPortfolioAnalyserConfig(configs: Array<any>): void {
		configs.forEach(config => this.storeService.fetch(config));
		// Retrieve models info
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		forkJoin([
			this.modelsEntitlementService.isEligibleForBLKManagedModels(),
			this.modelsEntitlementService.isEligibleForThirdPartyModels()
		]).pipe(
			switchMap(([isEligibleForBLKManagedModels, isEligibleForThirdPartyModels]) => {
				const handleError = catchError(error => {
					console.error('Error retrieving Models. Status code:', error.status, error);
					return of({});
				});

				const apisToCall = {
					defaultModels: this.apiService.fetchAllModels().pipe(handleError),
					managedModels: this.apiService.fetchManagedModelsConfig(),
					thirdParty: this.apiService.fetchThirdPartyModelsList().pipe(handleError)
				};

				const shouldUseManagedModels = isEligibleForBLKManagedModels;
				if (!shouldUseManagedModels) {
					delete apisToCall.managedModels;
				}

				if (!this.isTpmEnabledForDotCom) {
					const shouldUseThirdPartyApi = isEligibleForThirdPartyModels && this.contentService.getBoolean('ENABLE_THIRD_PARTY_API', true);
					if (!shouldUseThirdPartyApi) {
						delete apisToCall.thirdParty;
					}
				}

				const shouldUseDefaultModels = this.contentService.getBoolean('ENABLE_CUSTOM_MODELS_API', true);
				if (!shouldUseDefaultModels) {
					delete apisToCall.defaultModels;
				}

				return forkJoin([forkJoin(apisToCall), of(isEligibleForBLKManagedModels)]);

			})
		).subscribe(([responseJson, isEligibleForBLKManagedModels]) => {
			const response = this.getModelPortfoliosListResponse(responseJson, isEligibleForBLKManagedModels);
			// config response has no success or allDataReturned attribute, so skip error handle
			this.updateStoreFromResponse(configs, response, responseJson, true);
		});
	}

	/**
	 * Fetches security taxes details for the provided portfolio id with provided positions and updates the provided storeConfig with the response.
	 * If api call returns error, store is updated with [] and an error is logged in the console.
	 *
	 * @param storeConfig store to be updated
	 * @param uniqueId WTP unique id
	 * @param portfolioId portfolio id
	 * @param positions array of positions of the provided portfolio
	 */
	public fetchSecurityTaxesWithPositions<T>(storeConfig: Config<T>, uniqueId: string, portfolioId: string, positions: Array<ApiParamPosition>): void {
		this.storeService.fetch(storeConfig);
		const isPartnerPortfolio: boolean = portfolioId.includes('PARTNER@');
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.apiService.fetchSecurityTaxesWithPositions(positions, uniqueId, isPartnerPortfolio).subscribe(
			responseJson => {
				const response = this.getSecurityTaxesResponse(responseJson);
				this.updateTaxStoreFromResponse(storeConfig, response, portfolioId);
			},
			error => {
				this.storeService.upsertMany(storeConfig, []);
				console.error('error getting tax security details');
			}
		);
	}

	/**
	 * Add or Update store with response.
	 *
	 * If performanceToHoldingIdMap is provided, it checks if the id from the response (which means the one the API call was made with)
	 * is a key in performanceToHoldingIdMap (which means the id is performance id) and if it does, it gets the holdings id from the mapping
	 * and updates the response and store with it.

	 * @param configs store configs to update
	 * @param response response used to update the store configs
	 * @param responseJson raw response used to handleResponseError
	 * @param skipErrorHandler flag to skip error handling, defaulted to false
	 * @param performanceToHoldingIdMap mapping of performance id to holdings id
	 * @param portfoliosRequestParam array of portfolios sent as request; this is used to check if any failed holdings is part of the current portfolio or not
	 */
	public updateStoreFromResponse(
		configs: Array<ApiStoreConfiguration>,
		response: any,
		responseJson?: any,
		skipErrorHandler: boolean = false,
		performanceToHoldingIdMap?: Map<string, string>,
		portfoliosRequestParam?: Array<any>
	): void {
		if (!isEmpty(response)) {
			configs.forEach(config => {
				if (config.asOfDateType === DataServiceApiConstants.AS_OF_DATE_TYPES.PRE_APPROVED) {
					this.storeService.upsertMany(
						config,
						response.map(res =>
							Object.assign(
								{ id: DataServiceApiConstants.STORES_PREFIXES.REBALANCE_PREFIX + res.portfolioId },
								Object.assign(res, { id: DataServiceApiConstants.STORES_PREFIXES.REBALANCE_PREFIX + res.portfolioId })
							)
						)
					);
				} else if (config.asOfDateType === DataServiceApiConstants.AS_OF_DATE_TYPES.LATEST) {
					this.storeService.upsertMany(
						config,
						response.map(res =>
							Object.assign(
								{ id: DataServiceApiConstants.STORES_PREFIXES.LATEST_PREFIX + res.portfolioId },
								Object.assign(res, { id: DataServiceApiConstants.STORES_PREFIXES.LATEST_PREFIX + res.portfolioId })
							)
						)
					);
				} else {
					this.storeService.upsertMany(
						config,
						response.map(res => {

							const responsePortfolioId = String(res.portfolioId || res.id || res.uniqueId);

							// map the response to the holdings id if responsePortfolioId is performance id
							let portfolioId = responsePortfolioId;
							let responseToUpdate = cloneDeep(res);
							if (!isEmpty(performanceToHoldingIdMap)) {
								portfolioId = performanceToHoldingIdMap.get(responsePortfolioId) || responsePortfolioId;
								responseToUpdate = this.getUpdatedResponse(responsePortfolioId, cloneDeep(res), performanceToHoldingIdMap);
							}

							return Object.assign(
								{
									id: portfolioId,
									name: this.contentService.get(portfolioId, CommonDCR[portfolioId] || portfolioId)
								},
								Object.assign(responseToUpdate, { id: portfolioId })
							);
						})
					);
				}
				// throw error after update store, so components still render when not all data returned
				this.handleResponseError(responseJson, config.name, skipErrorHandler, portfoliosRequestParam);
			});
		} else {
			configs.forEach(config => {
				// unload store before throwing error
				this.storeService.upsertMany(config, []);
			});
			configs.forEach(config => {
				this.handleResponseError(responseJson, config.name, skipErrorHandler);
			});

		}
	}

	/**
	 * throw error to be handled by application handler while responseJson:
	 * success = false OR
	 * success = true, allDataReturned = false
	 */
	public handleResponseError(responseJson: any, configName: string, skipErrorHandler: boolean, portfoliosRequestParam?: Array<any>): void {
		const isHoldingMissing = responseJson?.errorCodes instanceof Array && responseJson.errorCodes.includes(ERROR_CODES.PORTFOLIO_ANALYSIS_HOLDING_NOT_FOUND);

		if (isHoldingMissing) {
			responseJson = this.updateMissingSecurityResponse(responseJson, portfoliosRequestParam);
		}

		if (
			!skipErrorHandler &&
			responseJson &&
			(!(responseJson.success && responseJson.allDataReturned) || isHoldingMissing)
		) {
			throw new ErrorObject(responseJson, configName);
		}
	}

	/**
	 * Based on the provided store configs and analysis params:
	 * - replaces performance related stores with performanceCommonPeriods store (which forces to make the performance call for common period)
	 * - replaces netPerformance related stores with netPerformanceCommonPeriods if performanceAnalysisParams
	 * (analysis params that use performance id instead of holdings id; performance id is used for managed models analysis) are provided
	 * - for each store config, it builds the api settings (including the rcs) and dispatches the data loader
	 *
	 * @param configs collection of store configurations used to drive api and store calls
	 * @param generalAnalysisParams analysis params used to build all calls*
	 * @param performanceAnalysisParams analysis params used to build performance calls; if they are provided, they will be used for performance calls instead of the general ones
	 * @param portfolioAnalysisOptions analysis options used to build API calls
	 */
	public loadPortfolioAnalysisData(
		configs: Array<ApiStoreConfiguration>,
		generalAnalysisParams: AnalysisParams,
		performanceAnalysisParams?: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		const shouldAddNetPerformanceStore = !isNil(performanceAnalysisParams);
		configs = this.combinePerformanceAndCommonPeriodsConfiguration(configs, shouldAddNetPerformanceStore);
		this.dispatchAllDataLoaders(configs, generalAnalysisParams, performanceAnalysisParams, portfolioAnalysisOptions);
	}

	/*************************************************
	 * Private methods
	 *************************************************/

	protected dispatchAllDataLoaders(
		configs: Array<ApiStoreConfiguration>,
		generalAnalysisParams: AnalysisParams,
		performanceAnalysisParams?: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		configs.forEach(config => {
			this.storeService.fetch(config);
			const apiSettings: ApiSettings = {
				rcs: this.generateRcs([config]),
				portfolioAnalysisParams: null,
				config: config
			};
			this.dispatchDataLoaders(apiSettings, generalAnalysisParams, performanceAnalysisParams, portfolioAnalysisOptions);
		});
	}

	/**
	 * Returns the security taxes response based on the provided responseJson with expected format responseJson.resultMap.
	 *
	 * @param responseJson
	 * @returns responseJson.resultMap if valid response with expected format, null otherwise
	 */
	private getSecurityTaxesResponse(responseJson: any): any[] {
		if (responseJson && responseJson.success && responseJson.allDataReturned) {
			const substitutionTickersToIgnore = this.contentService.get('TAX_SUBSTITUTION_TICKER_TO_IGNORE', 'CASH-USD').split(',');
			return TaxDataUtil.getValidResultMap(get(responseJson, 'resultMap', null), substitutionTickersToIgnore);
		}
	}

	/**
	 * Updates any performance id occurrences with the corresponding holdings id from performanceToHoldingIdMap in the provided response and returns it.
	 * If the response does not have any performance id for models either at the response level or as part of a blend, the provided response is returned.
	 *
	 * @param responsePortfolioId id in response (can either be holdings id or performance id)
	 * @param responseToUpdate
	 * @param performanceToHoldingIdMap mapping of any existent performance id to holding id
	 */
	private getUpdatedResponse(responsePortfolioId: string, responseToUpdate: any, performanceToHoldingIdMap: Map<string, string>): any {
		const debugDataEntries = Object.entries(get(responseToUpdate, 'returns.debugData', {}));
		const parentHoldingsId = performanceToHoldingIdMap.get(responsePortfolioId);

		const isModelAnalysed = !isNil(parentHoldingsId);

		let returnsToUpdate = cloneDeep(get(responseToUpdate, 'returns', {}));

		// update debugData if provided
		if (!isEmpty(debugDataEntries)) {
			const debugData = this.updateDebugData(debugDataEntries, performanceToHoldingIdMap);
			returnsToUpdate = { ...returnsToUpdate, debugData };
		}

		// update response ids if model is analysed directly (not as part of blend)
		if (isModelAnalysed) {
			const uniqueId = get(returnsToUpdate, 'uniqueId');
			const updatedId = replace(uniqueId, responsePortfolioId, parentHoldingsId);
			returnsToUpdate = { ...returnsToUpdate, uniqueId: updatedId };

			if (responseToUpdate.portfolioId) { // update portfolioId if provided (not provided for portfolios)
				responseToUpdate = { ...responseToUpdate, portfolioId: +parentHoldingsId };
			}

			if (responseToUpdate.identifier) { // update portfolioId if provided (not provided for portfolios)
				responseToUpdate = { ...responseToUpdate, identifier: parentHoldingsId };
			}
		}

		return Object.assign({}, responseToUpdate, { returns: { ...returnsToUpdate } });
	}

	/**
	 * For the provided debugDataEntries, checks if any of the debug data ids are performance ids in performanceToHoldingIdMap and:
	 * - if debug id is performance id, it replaces all occurrences of it with holdings id
	 * - if debug id is not performance id, does not do any change
	 *
	 * @param debugDataEntries debug data entries to update (['id', {startDate: 20200102, returnsId: modelId ...}])
	 * @param performanceToHoldingIdMap
	 */
	private updateDebugData(debugDataEntries: any, performanceToHoldingIdMap: Map<string, string>): any {
		const performanceIdsToMap = Array.from(performanceToHoldingIdMap.keys());

		const updatedDebugDataEntries = debugDataEntries.map(([portfolioKey, portfolioValue]: any[]) => {

			const performanceIdToMap = performanceIdsToMap.find(performanceId => endsWith(portfolioKey, ':' + performanceId));
			if (!isNil(performanceIdToMap)) {
				const holdingsId = performanceToHoldingIdMap.get(performanceIdToMap);
				const updatedId = replace(portfolioKey, performanceIdToMap, holdingsId); // can either be PORTFOLIO_ID:modelId if models is analysed or MODEL_ID:modelId if model is analysed as part of blend

				return [updatedId, { ...portfolioValue, returnsId: holdingsId }];
			} else {
				return [portfolioKey, portfolioValue];
			}
		});

		// transform the entries to object
		return updatedDebugDataEntries.reduce((acc, [key, value]) => {
			acc[key] = value;
			return acc;
		}, {});
	}

	/**
	 * Updates the provided store with the provided response.
	 * If the response is null / undefined, an error is logged in the console.
	 *
	 * @param storeConfig store to be updated
	 * @param response response to be added to the store
	 * @param portfolioId portfolioId (optional)
	 */
	private updateTaxStoreFromResponse<T>(storeConfig: Config<T>, response: any, portfolioId?: string): void {
		if (response) {
			const objectToAdd = Object.assign(response, { id: portfolioId });
			this.storeService.upsertOne(storeConfig, objectToAdd);
		} else {
			this.storeService.upsertMany(storeConfig, []);
			console.error('error getting tax security details');
		}
	}

	/**
	 * Handle the response from /wds/api/config-file/bfa:model-portfolios
	 * Comment out error handling for now, need to check how to isolate the services
	 */
	private getModelPortfoliosListResponse(responseJson: any, shouldFilterResearchBlkModel: boolean = false): any {
		if (!responseJson) {
			throw new Error(this.contentService.get('PORTFOLIO_ANALYSER_CONFIG_ERROR', ErrorDCR.PORTFOLIO_ANALYSER_CONFIG_ERROR));
		} else {
			const response: Map<string, any> = new Map(); // using a map to preserve the order
			const rootViewObject = {};
			const defaultModels = responseJson.defaultModels?.response || [];
			const defaultPortfolios = compact(flatten(defaultModels.map(family => family.portfolios))); // listing all ADAM portfolios to update managed models name source

			const managedModelsResponse = responseJson.managedModels || {};
			if (!isEmpty(managedModelsResponse)) {
				rootViewObject['vendorId'] = managedModelsResponse['id'];
				rootViewObject['rebalanceGlitch'] = managedModelsResponse['rebalanceGlitch'];
				rootViewObject['showLatestRebalanceLink'] = managedModelsResponse['showLatestRebalanceLink'];
				rootViewObject['reportTemplate'] = managedModelsResponse['reportTemplate'];

				get(managedModelsResponse, 'portfolioFamilies', []).forEach(family => {
					const familyObject = {
						showBenchmark: family.showBenchmark,
						isDefaultFamilyForVehiclePref: family.isDefault,
						vehicle1: family.vehicle1,
						vehicle2: family.vehicle2,
						familyCode: family.portfolioFamilySchema.code,
						familyName: family.portfolioFamilySchema.name,
						rebalDocId: family.portfolioFamilySchema.rebalDocId,
						docIds: family.portfolioFamilySchema.docIds,
						subType: 'BLK',
						enableAdamModelNames: this.enableAdamModelNames
					};

					get(family, 'portfolioFamilySchema.portfolios', []).forEach(portfolio => {
						const portfolioObject = {
							...rootViewObject,
							...familyObject,
							...portfolio,
							name: this.updateToAdamModelName(String(portfolio.id), defaultPortfolios) || portfolio.name,
							dasPortfolioId: portfolio.id,
							aladdinPortfolioCode: this.mapManagedModelDasIdToAPCFromDefaultModels(String(portfolio.id), defaultModels) || portfolio.aladdinPortfolioCode,
							groups: [{
								id: SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP,
								name: SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP_TITLE
							}] // Adding default blk group to the managed models as filtering out models without groups array instead of mapping them to blk model
						};
						response.set(String(portfolioObject.id), portfolioObject);
					});
				});
			}

			const thirdPartyResponse = get(responseJson, 'thirdParty.response', []);
			if (!isEmpty(thirdPartyResponse)) {
				thirdPartyResponse.forEach(family => {
					const familyObject = {
						familyCode: family.id,
						familyName: family.description,
						isDefaultFamilyForVehiclePref: true,
						subType: 'TPM'
					};

					get(family, 'portfolios', []).forEach(portfolio => {
						const portfolioObject = {
							...familyObject,
							id: portfolio.id,
							name: portfolio.description,
							managementType: portfolio?.allData?.managementType
						};
						response.set(String(portfolioObject.id), portfolioObject);
					});
				});
			}

			if (!isEmpty(defaultModels)) {
				defaultModels
					.filter(family => !shouldFilterResearchBlkModel || family?.subType !== 'BLK')
					.forEach(family => {
						const familyObject = {
							familyCode: family.id,
							familyName: family.description,
							isDefaultFamilyForVehiclePref: true,
							subType: family.subType,
							groups: family.groups,
							enableAdamModelNames: this.enableAdamModelNames,
							enableCmsRenaming: this.enableCmsRenaming,
							displayOrder: family.allData?.displayOrder,
							modelExternalFamilyName: family.allData?.modelExternalName
						};

						get(family, 'portfolios', []).forEach(portfolio => {
							const managementType = portfolio?.managementType || portfolio?.allData?.managementType || family.allData?.managementType;
							const id = this.flipModelsConfigStoreIdentifierToDasPortfolioId ? portfolio?.allData?.portfolioId : portfolio.id;
							const portfolioObject = {
								...familyObject,
								id,
								name: portfolio.description,
								managementType: managementType,
								groups: portfolio.groups || family.groups,
								dasModelOrder: portfolio?.allData?.dasModelOrder,
								shortName: portfolio?.allData?.shortName,
								modelExternalName: portfolio?.allData?.modelExternalName,
								dasPortfolioId: portfolio?.allData?.portfolioId,
								aladdinPortfolioCode: portfolio.id
							};

							if (portfolio.allData?.performanceId && managementType?.toLowerCase() === 'managed') {
								portfolioObject['performanceId'] = portfolio.allData?.performanceId;
								if (!isEmpty(family.allData?.documentIds)) {
									portfolioObject['docIds'] = family.allData?.documentIds?.map(document => {
										const documentSplitArr = split(document, ':', 2);
										return {
											materialType: head(documentSplitArr),
											docId: last(documentSplitArr)
										};
									});
								}
							}

							const portfolioId = String(portfolioObject.id);
							if (response.has(portfolioId)) {
								Object.assign(response.get(portfolioId), portfolioObject);
							} else {
								response.set(portfolioId, portfolioObject);
							}
						});
					});
			}

			return Array.from(response.values());
		}
	}

	/**
	 * Update the managed model name source to ADAM models
	 */
	private updateToAdamModelName(id: string, portfolios: PortfolioData[]): string {
		const adamPortfolio = portfolios.find(portfolio => (portfolio.allData?.portfolioId || portfolio.id) === id);
		return adamPortfolio?.description;
	}
	/**
	 * Handle the response from /wds/services/portfolio-analysis
	 */
	private getPortfoliosAnalysisResponse(responseJson: any): any[] {
		const json = get(responseJson, 'resultMap.PORTFOLIOS[0]', {});
		return json.portfolios || [];
	}

	/**
	 * Checks if configs include performance related stores and replaces them with performance common periods ones.
	 * This is done so that the first performance call that is made is done for common periods,
	 * and if that is successful individual performance calls will be made for each portfolio.
	 *
	 * Common period performance call is important when we analyse multiple portfolios.
	 *
	 * If configs includes performance or performanceCommonPeriods, it filters them out and adds performanceCommonPeriods.
	 * If configs includes netPerformance or netPerformanceCommonPeriods and shouldAddNetPerformanceStore is true, it filters them out and adds netPerformanceCommonPeriods.
	 *
	 * @param configs initial store configs
	 * @param shouldAddNetPerformanceStore true if the netPerformanceCommonPeriods store should be added, false otherwise
	 */
	private combinePerformanceAndCommonPeriodsConfiguration(configs: Array<ApiStoreConfiguration>, shouldAddNetPerformanceStore: boolean): any {
		const performanceStores = [DataServiceApiConstants.STORES.PERFORMANCE, DataServiceApiConstants.STORES.PERFORMANCE_COMMON_PERIODS];
		const netPerformanceStores = [DataServiceApiConstants.STORES.NET_PERFORMANCE, DataServiceApiConstants.STORES.NET_PERFORMANCE_COMMON_PERIODS];

		const isPerformanceIncludedInConfigs = configs.some(config => performanceStores.includes(config.name));
		const isNetPerformanceIncludedInConfigs = configs.some(config => netPerformanceStores.includes(config.name));

		if (isPerformanceIncludedInConfigs) {
			configs = configs.filter(config => !performanceStores.includes(config.name));
			configs.push({ name: DataServiceApiConstants.STORES.PERFORMANCE_COMMON_PERIODS });
		}

		if (shouldAddNetPerformanceStore && isNetPerformanceIncludedInConfigs) {
			configs = configs.filter(config => !netPerformanceStores.includes(config.name));
			configs.push({ name: DataServiceApiConstants.STORES.NET_PERFORMANCE_COMMON_PERIODS });
		}

		return configs;
	}

	/**
	 * Update response with missing security to include shouldSkipEditFailingPortfolio flag.
	 * If the security missing (part of the response) is sent as part of the request => the security is missing at the portfolio level
	 * => shouldSkipEditFailingPortfolio is set to false
	 * If the security missing (part of the response) is not sent as part of the request => the security is missing as part of a model which is a child of the portfolio blend
	 * => shouldSkipEditFailingPortfolio is set to true
	 *
	 * shouldSkipEditFailingPortfolio is used in the MissingHoldingErrorHandlerService to show/ hide edit action button.
	 * If shouldSkipEditFailingPortfolio is true, edit action button is hidden; otherwise no change (edit is shown).
	 *
	 * @param responseJson response with missing security to be handled
	 * @param portfoliosRequestParam portfolio request params
	 */
	private updateMissingSecurityResponse(responseJson: any, portfoliosRequestParam?: Array<any>): any {
		const firstPortfolioFailedSecurities = responseJson?.resultMap?.PORTFOLIOS[0]?.portfolios?.find(portfolio => !!portfolio.errorCodes || !portfolio.success);
		const failedSecurities = firstPortfolioFailedSecurities?.holdings.filter(position => !position.success).map(position => position.uniqueId);

		const portfolioHoldings = portfoliosRequestParam?.find(requestPortfolioParam => requestPortfolioParam.id ?? requestPortfolioParam.uniqueId === firstPortfolioFailedSecurities.id);
		const portfolioSecurities = portfolioHoldings?.positions.map(position => position.identifier);

		const shouldSkipEditFailingPortfolio = !failedSecurities.some(failedSecurity => portfolioSecurities?.includes(failedSecurity));

		return { ...responseJson, shouldSkipEditFailingPortfolio };
	}

	/**
	 * Return combined rcs extract from config
	 */
	protected generateRcs(configs: Array<ApiStoreConfiguration>): any {
		return configs.map(config => DataServiceApiConstants.PORTFOLIO_ANALYSIS_RCS[config.name]).join(',');
	}

	/**
	 * For each portfolio provided in portfolios, it builds the params and makes the performance call based on the provided portfolioAnalysisOptions:
	 * - custom net if isNetPerformance is true and customAdvisoryFee is provided
	 * - net if isNetPerformance is true, gross otherwise,
	 * - gross otherwise
	 *
	 * NOTE: If optimisation is enabled:
	 * - if we only analyse one portfolio or model, then no extra individual API call will be made -> store will be updated with the same as we updated the common period store
	 * - if we analyse multiple portfolios/models, we will make the individual API call + sleeve one only for models/ blend with model in it
	 *
	 * If performance call is successful, it updates the store (performance for gross performance; netPerformance for net performance) with the response
	 * based on the performanceToHoldingIdMap provided as part of the portfolioAnalysisOptions.
	 *
	 * If provided portfolios array is empty/nil (this means the performance common period call is not successful),
	 * it updates both performance and performance common period stores (net or gross) with [].
	 *
	 * @param portfolios array of portfolios json
	 * @param analysisParams params used to build individual performance call
	 * @param asOfDateType defaulted to LATEST
	 * @param portfolioAnalysisOptions analysis options used to build performance calls
	 */
	private loadPerformancePerPortfolio(
		portfolios: Array<any>,
		analysisParams: AnalysisParams,
		asOfDateType: string = DataServiceApiConstants.AS_OF_DATE_TYPES.LATEST,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions,
		performanceSleeveAnalysisParams?: AnalysisParams
	): void {

		// get options for individual performance API call
		const performanceToHoldingIdMap = get(portfolioAnalysisOptions, 'performanceToHoldingIdMap');

		// set config store to update based on the options
		const config = this.getPerformanceStore(portfolioAnalysisOptions, false);

		if (!isEmpty(portfolios)) {
			// optimisation: if we only analyse one portfolio, skip the individual performance call as the response is the same
			if (this.shouldOptimisePerformanceApis && portfolios.length === 1) {
				this.updateStoreFromResponse([config], portfolios, portfolios[0], true, performanceToHoldingIdMap); // skipping error handling as it will be done for performance common period

			} else {
				portfolios.forEach(portfolio => {
					const baseRcs = this.generateRcs([config]);
					const params = this.getPerformancePerPortfolioParams(portfolio, analysisParams, asOfDateType, portfolioAnalysisOptions);
					const hasModelChild = params?.portfolios[0]?.positions?.some(position => PortfolioIdUtil.isModel(position.identifier));

					let isResearchModel = false;;
					performanceToHoldingIdMap?.forEach((key, value) => {
						if (value !== portfolio.portfolioId?.toString()) {
							isResearchModel = true;
						}
					});
					const isManagedModel = PortfolioIdUtil.isModel(portfolio.portfolioId) && !isResearchModel; // portfolioId is retrieved only for models

					// performanceSleeveAnalysisParams is needed for research model support
					const shouldOptimiseManagedModelPerformance = this.shouldOptimisePerformanceApis && performanceSleeveAnalysisParams && isManagedModel;
					const rcs = shouldOptimiseManagedModelPerformance || this.shouldMakeSimplePerformanceApiCall
						? baseRcs + DataServiceApiConstants.PORTFOLIO_ANALYSIS_SIMPLE_RCS_SUFFIX : baseRcs;
					const apisToCall = [
						this.apiService.fetchPortfolioAnalysis(rcs, params)
					];
					const shouldMakeIndividualApiCall = this.shouldOptimisePerformanceApis ? hasModelChild || isManagedModel : true;
					if (performanceSleeveAnalysisParams && shouldMakeIndividualApiCall) {
						apisToCall.push(
							this.apiService.fetchPortfolioAnalysis(
								baseRcs + DataServiceApiConstants.PORTFOLIO_ANALYSIS_SLEEVE_RCS_SUFFIX,
								this.getPerformancePerPortfolioParams(portfolio, performanceSleeveAnalysisParams, asOfDateType, portfolioAnalysisOptions)
							)
						);
					}
					// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
					return forkJoin(apisToCall)
						.pipe(timeout(SharedApplicationConstants.DEFAULT_REQUEST_TIMEOUT))
						.subscribe(responseJson => {
							const response = this.getPerformanceUpdatedResponse(responseJson);

							this.updateStoreFromResponse([config], response, responseJson[0], false, performanceToHoldingIdMap, params['portfolios']);
						});

				});
			}
		} else {
			// unload performance and performance common period (gross or net or custom net) stores
			const configsToUpdate = [config, this.getPerformanceStore(portfolioAnalysisOptions, true)];
			this.updateStoreFromResponse(configsToUpdate, [], []);
		}
	}

	private getPerformancePerPortfolioParams(
		portfolio: any, analysisParams: AnalysisParams, asOfDateType: string, portfolioAnalysisOptions: PortfolioAnalysisOptions
	): any {
		const params = {
			asOfDateType: asOfDateType,
			endDate: portfolio?.returns?.endDate, // endDate will be the same for all portfolios response from performance common periods API call
			betaPortfolios: JSON.stringify(BenchmarkConfig.betaPortfolios),
			netReturnFee: portfolioAnalysisOptions?.customAdvisoryFee // this is only provided if partner portfolio provides custom advisory fee
		};

		const portFromParam = analysisParams.analysisParamsArray.find(port => {
			const isUserPortfolio = port.uniqueId === portfolio.id;
			const portfolioId = get(port, 'portfolioId') || '';
			const isBlkModel = `PORTFOLIO_ID:${portfolioId}` === portfolio.id;
			const isThirdPartyModel = PortfolioIdUtil.isThirdPartyModel(portfolioId) && portfolioId === portfolio.id;

			let isSleeveManagedModelId;
			portfolioAnalysisOptions?.performanceToHoldingIdMap?.forEach((value, key) => {
				if (`PORTFOLIO_ID:${key}` === portfolio.id && value === portfolioId) {
					isSleeveManagedModelId = true;
				}
			});

			return isUserPortfolio || isBlkModel || isThirdPartyModel || isSleeveManagedModelId;
		});

		params['portfolios'] = BenchmarkUtil.getPortfoliosParamsConfig([portFromParam]);
		return params;
	}

	/**
	 * Dispatches the appropriate service handler to populate the corresponding store.
	 * For the performance calls (gross or net), it checks if performanceAnalysisParams are provided, and if they are,
	 * they will be used to build the performance calls instead of the generalAnalysisParams.
	 *
	 * PerformanceAnalysisParams are provided when user is a partner user.
	 * If a managed model is analysed either individually or as part of a blend, performanceAnalysisParams are built with performance id instead of holdings id.
	 *
	 * @param apiSettings api settings included the rcs to make the call with
	 * @param generalAnalysisParams analysis params used to build all calls*
	 * @param performanceAnalysisParams analysis params used to build performance calls; if they are provided, they will be used for performance calls instead of the general ones
	 * @param portfolioAnalysisOptions analysis options used to build API calls
	 */
	protected dispatchDataLoaders(
		apiSettings: ApiSettings,
		generalAnalysisParams: AnalysisParams,
		performanceAnalysisParams?: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		const apiSettingMethodName = apiSettings.config?.dataLoaderMethodName || apiSettings.config?.name;
		switch (apiSettingMethodName) {
			case DataServiceApiConstants.STORES.PERFORMANCE_COMMON_PERIODS:
				const performanceCommonPeriodsAnalysisParams = performanceAnalysisParams || generalAnalysisParams;

				this.loadPerformanceCommonPeriodsData(
					apiSettings, performanceCommonPeriodsAnalysisParams, portfolioAnalysisOptions, this.getPerformanceSleeveAnalysisParams(portfolioAnalysisOptions, generalAnalysisParams)
				);
				break;
			case DataServiceApiConstants.STORES.NET_PERFORMANCE_COMMON_PERIODS:
				const netPerformanceCommonPeriodsAnalysisParams = performanceAnalysisParams || generalAnalysisParams;
				portfolioAnalysisOptions = { ...portfolioAnalysisOptions, isNetPerformance: true };

				this.loadPerformanceCommonPeriodsData(
					apiSettings, netPerformanceCommonPeriodsAnalysisParams, portfolioAnalysisOptions, this.getPerformanceSleeveAnalysisParams(portfolioAnalysisOptions, generalAnalysisParams)
				);
				break;
			case DataServiceApiConstants.STORES.NET_CUSTOM_PERFORMANCE_COMMON_PERIODS:
				const netCustomPerformanceCommonPeriodsAnalysisParams = performanceAnalysisParams || generalAnalysisParams;
				portfolioAnalysisOptions = { ...portfolioAnalysisOptions, isNetPerformance: true };

				this.loadPerformanceCommonPeriodsData(
					apiSettings,
					netCustomPerformanceCommonPeriodsAnalysisParams,
					portfolioAnalysisOptions,
					this.getPerformanceSleeveAnalysisParams(portfolioAnalysisOptions, generalAnalysisParams)
				);
				break;
			case DataServiceApiConstants.STORES.MB_DETAILS:
				apiSettings.rcs = DataServiceApiConstants.PORTFOLIO_ANALYSIS_RCS.mbDetails;
				this.loadDefaultStoreData(apiSettings, generalAnalysisParams);
				break;
			default:
				this.loadDefaultStoreData(apiSettings, generalAnalysisParams);
				break;
		}
	}

	private getPerformanceSleeveAnalysisParams(portfolioAnalysisOptions: PortfolioAnalysisOptions, generalAnalysisParams: AnalysisParams): AnalysisParams {
		return !isEmpty(portfolioAnalysisOptions?.performanceToHoldingIdMap) && portfolioAnalysisOptions?.shouldMakePerformanceSleeveApiCall ? generalAnalysisParams : null;
	}

	/**
	 * Using the provided apiSettings and analysisParams, it builds and makes performance call for common period:
	 * - net if isNetPerformance is true
	 * - net if isNetPerformance is true and customAdvisoryFee is provided
	 * - gross otherwise
	 *
	 * This call will return the performance for the common period we have data when we run multiple portfolios.
	 * This call will return the same results such as the individual performance call if we only analyze one portfolio.
	 *
	 * @param apiSettings api settings included the rcs to make the call with
	 * @param analysisParams analysis params to build the api call
	 * @param portfolioAnalysisOptions analysis options used to build performance calls
	 */
	private loadPerformanceCommonPeriodsData(
		apiSettings: ApiSettings,
		analysisParams: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions,
		performanceSleeveAnalysisParams?: AnalysisParams
	): void {

		// check if individual performance should be skipped and update the performanceStores based on this
		const shouldSkipPerformancePerPortfolio = portfolioAnalysisOptions?.shouldSkipPerformancePerPortfolio;
		const allPerformanceStores = [apiSettings.config];

		// if individual performance should not be skipped, update stores with individual performance for removing previous performance info/ error handling update
		if (!shouldSkipPerformancePerPortfolio) {
			allPerformanceStores.push(this.getPerformanceStore(portfolioAnalysisOptions, false));
		}

		// if previous performance should be persisted, do not remove previous performance info from store
		// NOTE: removing previous performance from store should be done to ensure we don't show incorrect data; only current case when this is skipped is for benchmark enhancement
		if (!portfolioAnalysisOptions?.shouldPersistPreviousPerformanceInStore) {
			// remove any performance info we have stored for the current ids from previous calls to reflect accurate data
			const performanceIds = get(analysisParams, 'analysisParamsArray', []).map(analysisParam => analysisParam.uniqueId || analysisParam.portfolioId);
			let ids = performanceIds;

			// set ids to holdings id (used to update the store)
			if (!isEmpty(portfolioAnalysisOptions?.performanceToHoldingIdMap)) {
				ids = performanceIds.map(id => portfolioAnalysisOptions.performanceToHoldingIdMap.get(id) || id);
			}
			allPerformanceStores.forEach(performanceStore => {
				this.storeService.removeMany(performanceStore, ids);

				// reset loading state of store
				this.storeService.fetch(performanceStore);
			});
		}

		// if we only analyse one managed model at a time, then use the simple performance rcs
		const modelId = analysisParams.analysisParamsArray[0].portfolioId;
		let isResearchModel;
		portfolioAnalysisOptions?.performanceToHoldingIdMap?.forEach((key, value) => {
			if (value !== modelId?.toString()) {
				isResearchModel = true;
			}
		});
		const isSingleManagedModelAnalysed = analysisParams.analysisParamsArray.length === 1 && PortfolioIdUtil.isModel(modelId) && !isResearchModel;

		const shouldOptimiseManagedModelPerformance = this.shouldOptimisePerformanceApis && performanceSleeveAnalysisParams && isSingleManagedModelAnalysed;
		const rcs = shouldOptimiseManagedModelPerformance || this.shouldMakeSimplePerformanceApiCall ?
			apiSettings.rcs + DataServiceApiConstants.PORTFOLIO_ANALYSIS_SIMPLE_RCS_SUFFIX : apiSettings.rcs;

		const apisToCall = [
			this.apiService.fetchPortfolioAnalysis(rcs, this.getPerformanceCommonPeriodParams(apiSettings, analysisParams, portfolioAnalysisOptions))
		];
		if (performanceSleeveAnalysisParams) {
			apisToCall.push(
				this.apiService.fetchPortfolioAnalysis(
					apiSettings.rcs + DataServiceApiConstants.PORTFOLIO_ANALYSIS_SLEEVE_RCS_SUFFIX,
					this.getPerformanceCommonPeriodParams(apiSettings, performanceSleeveAnalysisParams, portfolioAnalysisOptions)
				)
			);
		}
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		forkJoin(apisToCall)
			.pipe(timeout(SharedApplicationConstants.DEFAULT_REQUEST_TIMEOUT), map(responseJson => [responseJson, analysisParams]))
			.subscribe(
				([responseJson, params]) => {
					const response = this.getPerformanceUpdatedResponse(responseJson);

					if (!shouldSkipPerformancePerPortfolio) {
						this.loadPerformancePerPortfolio(
							response, (<AnalysisParams>params), DataServiceApiConstants.AS_OF_DATE_TYPES.LATEST, portfolioAnalysisOptions, performanceSleeveAnalysisParams
						);
					}
					this.updateStoreFromResponse(
						[apiSettings.config], response, responseJson[0], false, portfolioAnalysisOptions?.performanceToHoldingIdMap, (<AnalysisParams>params).analysisParamsArray
					);
				},
				error => {
					// update all performance stores with [] to set isLoading to false
					allPerformanceStores.forEach(performanceStore => this.storeService.upsertMany(performanceStore, []));

					this.handleResponseError({ success: false, error: error }, apiSettings.config.name, false);
				}
			);
	}

	/**
	 * Builds and returns the analysis params needed for the performance common period API call.
	 * @param apiSettings
	 * @param analysisParams
	 * @param portfolioAnalysisOptions
	 * @returns analysis params needed for the performance common period API call
	 */
	private getPerformanceCommonPeriodParams(apiSettings: ApiSettings, analysisParams: AnalysisParams, portfolioAnalysisOptions?: PortfolioAnalysisOptions): AnalysisParams {
		const customAdvisoryFee = portfolioAnalysisOptions?.customAdvisoryFee;

		let params: any = { ...BenchmarkUtil.getDefaultBenchmark(apiSettings.config, analysisParams) };
		if (portfolioAnalysisOptions?.isNetPerformance && customAdvisoryFee) {
			params = { ...params, netReturnFee: customAdvisoryFee };
		}

		return params;
	}

	/**
	 * It returns the updated performance (common period or individual) response.
	 *
	 * If the provided responseJson has performanceSleeveResponse, it returns the performanceResponse + relativePortfolios and holdings
	 * from the performanceSleeveResponse (needed for models), otherwise, it returns the performanceResponse as it is.
	 *
	 * @param responseJson
	 * @returns the updated performance response
	 */
	private getPerformanceUpdatedResponse(responseJson: any): any {
		const performanceResponse = this.getPortfoliosAnalysisResponse(responseJson[0]);
		const performanceSleeveResponse = this.getPortfoliosAnalysisResponse(responseJson[1]);

		let response = performanceResponse;
		if (!isEmpty(performanceSleeveResponse)) {
			response = performanceResponse.map((portfolioResponse, i) => {
				return {
					...portfolioResponse,
					relativePortfolios: performanceSleeveResponse[i].relativePortfolios,
					holdings: performanceSleeveResponse[i].holdings
				};
			});
		}

		return response;
	}

	/**
	 * Method used to get the performance store to update based on the provided options.
	 * This is used by all performance related API calls for updating the store in case of success & error handling.
	 *
	 * @param portfolioAnalysisOptions analysis options used to build performance calls
	 * @param isCommonPeriodStore true if store is for common period call, false otherwise
	 */
	private getPerformanceStore(portfolioAnalysisOptions: PortfolioAnalysisOptions, isCommonPeriodStore: boolean): ApiStoreConfiguration {
		const isNetPerformance = get(portfolioAnalysisOptions, 'isNetPerformance');
		const customAdvisoryFee = get(portfolioAnalysisOptions, 'customAdvisoryFee');

		// defining stores for individual and common periods performance
		const performanceStores = {
			'gross': DataServiceApiConstants.STORES.PERFORMANCE,
			'net': DataServiceApiConstants.STORES.NET_PERFORMANCE,
			'netCustom': DataServiceApiConstants.STORES.NET_CUSTOM_PERFORMANCE
		};
		const commonPeriodsStores = {
			'gross': DataServiceApiConstants.STORES.PERFORMANCE_COMMON_PERIODS,
			'net': DataServiceApiConstants.STORES.NET_PERFORMANCE_COMMON_PERIODS,
			'netCustom': DataServiceApiConstants.STORES.NET_CUSTOM_PERFORMANCE_COMMON_PERIODS
		};

		const stores = isCommonPeriodStore ? commonPeriodsStores : performanceStores;

		if (isNetPerformance && customAdvisoryFee) {
			return { name: stores['netCustom'] };
		} else if (isNetPerformance) {
			return { name: stores['net'] };
		} else {
			return { name: stores['gross'] };
		}
	}

	/**
	 * @description loadDefaultStoreData: performs the appropriate api calls and updates the corresponding stores.
	 * @param {apiSettings} apiSettings: api specific settings: rcs, config, and portfolioAnalysisParams.
	 * @param {AnalysisParams} analysisParams: the params used to build the api call.
	 */
	protected loadDefaultStoreData(apiSettings: ApiSettings, analysisParams: AnalysisParams): void {
		apiSettings.portfolioAnalysisParams = BenchmarkUtil.getDefaultBenchmark(apiSettings.config, analysisParams);
		this.dispatchFetchPortfolioAnalysisApiCall(apiSettings);
	}

	/**
	 * @description dispatchFetchPortfolioAnalysisApiCall: performs the fetchPortfolioAnalysis api call and updates the corresponding stores.
	 * @param {apiSettings} apiSettings: api specific settings: rcs, config, and portfolioAnalysisParams.
	 */
	protected dispatchFetchPortfolioAnalysisApiCall(apiSettings: ApiSettings, skipErrorHandler: boolean = false): void {
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.apiService
			.fetchPortfolioAnalysis(apiSettings.rcs, apiSettings.portfolioAnalysisParams)
			.pipe(timeout(SharedApplicationConstants.DEFAULT_REQUEST_TIMEOUT))
			.subscribe(
				responseJson => {
					const portfolios = this.getPortfoliosAnalysisResponse(responseJson);
					this.checkHoldingsWeights(responseJson, portfolios, apiSettings);

					const portfoliosRequest = JSON.parse(apiSettings.portfolioAnalysisParams.portfolios);
					this.updateStoreFromResponse([apiSettings.config], portfolios, responseJson, skipErrorHandler, null, portfoliosRequest);
				},
				error => {
					this.storeService.upsertMany(apiSettings.config, []);
					this.handleResponseError({ success: false, error: error }, apiSettings.config.name, skipErrorHandler);
				}
			);
	}

	private checkHoldingsWeights(responseJson: any, portfolios: any, apiSettings: ApiSettings): void {
		if (!find(portfolios, (portfolio) => portfolio.id === 'current-customization')) {
			portfolios.forEach(portfolio => {
				const sumWeights = sumBy(portfolio.holdings, 'weight');
				const tolerance = Number(this.contentService.get('HOLDINGS_ERROR_WEIGHT_TOLERANCE', DataPortfolioAnalysisService.WEIGHT_TOLERANCE.toString()));
				if (
					portfolio.holdings &&
					sumWeights > 0 &&
					Math.abs(100 - sumWeights) > tolerance
				) {
					this.addErrorCodesToObject(portfolio, ERROR_CODES.HOLDINGS_TOTAL_WEIGHT_ERROR);
				}
			});

			if (find(portfolios, (portfolio) => (portfolio.errorCodes || []).includes(ERROR_CODES.HOLDINGS_TOTAL_WEIGHT_ERROR))) {
				this.addErrorCodesToObject(responseJson, ERROR_CODES.HOLDINGS_TOTAL_WEIGHT_ERROR);
				throw new ErrorObject(responseJson, apiSettings.config.name);
			}
		}
	}

	private addErrorCodesToObject(object, errorCode): void {
		object.errorCodes = object.errorCodes ? [...object.errorCodes, errorCode] : [errorCode];
	}

	private mapManagedModelDasIdToAPCFromDefaultModels(dasId: string, defaultModels) {
		let apc;

		for (const family of defaultModels) {
			const foundId = family.portfolios?.find(model => dasId === model.allData.portfolioId)?.id;
			if (!isNil(foundId)) {
				apc = foundId;
				break;
			}
		}
		return apc;
	}
}
